/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useUser } from '../UNSAFE_useUser';
import { useTheme } from '../UNSAFE_useTheme';
import { useColorScheme } from '../UNSAFE_useColorScheme';
import { useScale } from '../UNSAFE_useScale';

function getCssVarName(name: string) {
  const rxp = name.match(/var\((--[a-zA-Z_0-9\-]+)\)/);
  return rxp && rxp.length > 1 ? rxp[1] : '';
}

/**
 * Hook to resolve the css vars.
 * @param vars
 * @param classes
 * @returns
 */
export function useCssVars(
  vars: {
    /**
     * The token variable.
     */
    tokenVar: string;
    /**
     * The key of the resolved variable.
     */
    key: string;
    /**
     * The absolute used value of the token vars assigned to css expression cannot be computed through getComputedStyle if
     * not assigned to a css property. This optional prop provides css property to assign the token var to in order to get
     * actual used style.
     */
    cssProp?: string;
  }[]
) {
  const cssDivRef = useRef<HTMLDivElement>(null);
  const [resolvedVars, setResolvedVars] = useState<Record<string, any>>({});
  const { forcedColors } = useUser();
  const { name } = useTheme();
  const scale = useScale();
  const colorScheme = useColorScheme();

  // A css token var (eg, --tokenVar_123xyz) that is assigned to a css expression like min(25px, 5rem) resolves to
  // a string of the expression (resolved value) like "min(25px, 5rem)" and not absolute value (for eg 25px) (used value) when getComputedStyle is called.
  // We have to assign the token var to a valid css property(like width: var(--tokenVar_123xyz)) in order to get actual used value in
  // computed styles. https://developer.mozilla.org/en-US/docs/Web/CSS/used_value

  // build a style obj with valid css property to apply to div
  const style = vars.reduce((prev: Record<string, string>, current) => {
    if (current.cssProp) {
      prev[current.cssProp] = current.tokenVar;
    }
    return prev;
  }, {});

  const cssContent = <div ref={cssDivRef} style={{ display: 'none', ...style }}></div>;

  useLayoutEffect(() => {
    if (!cssDivRef.current) {
      return;
    }

    const style = window.getComputedStyle(cssDivRef.current);
    const resolved: Record<string, any> = {};
    vars.forEach((value) => {
      // use cssProp for token vars that resolve to expression
      const cssVar = value.cssProp || getCssVarName(value.tokenVar);
      resolved[value.key] = style.getPropertyValue(cssVar);
    });
    setResolvedVars(resolved);
  }, [forcedColors, name, colorScheme, scale, vars]);

  return {
    resolvedVars,
    cssContent
  };
}
